import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { User, UserService } from '../../shared/services/user.service';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './users.component.html'
})
export class UsersComponent implements OnInit {
  private fb = inject(FormBuilder);
  private userService = inject(UserService);

  users: User[] = [];
  form!: FormGroup;
  editingId: number | null = null;
  loading = false;
  error: string | null = null;

  ngOnInit(): void {
    this.form = this.fb.group({
      username: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', []],
      roles: this.fb.control<string | string[]>('') // comma-separated roles or array
    });
    this.loadUsers();
  }

  get isEditMode(): boolean {
    return this.editingId != null;
  }

  loadUsers() {
    this.loading = true;
    this.userService.list().subscribe({
      next: (data) => { this.users = data; this.loading = false; },
      error: (err) => { this.error = err?.message ?? 'Failed to load users'; this.loading = false; }
    });
  }

  onSubmit() {
    if (this.form.invalid) return;
    const value = this.form.value as any;
    // Support roles entered as comma-separated string or array
    const roles = Array.isArray(value.roles)
      ? value.roles
      : (value.roles ? String(value.roles).split(',').map((r: string) => r.trim()).filter(Boolean) : []);

    const payload = {
      username: value.username,
      email: value.email,
      password: value.password,
      roles
    };

    if (this.isEditMode && this.editingId != null) {
      this.userService.update(this.editingId, payload).subscribe({
        next: () => { this.resetForm(); this.loadUsers(); },
        error: (err) => this.error = err?.message ?? 'Failed to update user'
      });
    } else {
      this.userService.create(payload).subscribe({
        next: () => { this.resetForm(); this.loadUsers(); },
        error: (err) => this.error = err?.message ?? 'Failed to create user'
      });
    }
  }

  edit(user: User) {
    this.editingId = user.id ?? null;
    this.form.patchValue({
      username: user.username,
      email: user.email,
      password: '', // leave password empty unless changing
      roles: user.roles?.join(', ')
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  cancelEdit() {
    this.resetForm();
  }

  resetForm() {
    this.editingId = null;
    this.form.reset({ username: '', email: '', password: '', roles: '' });
  }

  confirmDelete(user: User) {
    const ok = window.confirm(`Are you sure you want to delete user "${user.username}"?`);
    if (!ok || user.id == null) return;
    this.userService.delete(user.id).subscribe({
      next: () => this.loadUsers(),
      error: (err) => this.error = err?.message ?? 'Failed to delete user'
    });
  }
}
